/**
 * Page controls for a card grid.
 *
 * Compact on purpose: it sits in a row that already carries tabs or filters, so it shows the range
 * in words ("13–24 de 57") rather than a strip of numbered buttons. The range matters more than
 * the page number — it is what tells someone whether what they are looking for is behind them.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';
import { IconButton } from '../IconButton/IconButton';

const wrapperStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  white-space: nowrap;
`;

const rangeStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
`;

export interface PaginationProps {
  /** 1-based. */
  page: number;
  /** Items per page — the size asked for, not how many came back. */
  pageSize: number;
  /** Items in the whole result, not in this page. */
  total: number;
  onChange: (page: number) => void;
  /** Prefix for the button ids, so two paginations on one screen stay distinguishable. */
  id: string;
}

export const Pagination = ({
  page,
  pageSize,
  total,
  onChange,
  id,
}: PaginationProps): JSX.Element | null => {
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  // Nothing to page through: the controls would only take space and suggest there is more.
  if (total <= pageSize) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className={wrapperStyle}>
      <span className={rangeStyle}>
        {first}–{last} de {total}
      </span>
      <IconButton
        id={`${id}-prev`}
        icon='Arrows.CaretLeft'
        size='sm'
        aria-label='Página anterior'
        disabled={page <= 1}
        onClick={() => {
          onChange(page - 1);
        }}
      />
      <IconButton
        id={`${id}-next`}
        icon='Arrows.CaretRight'
        size='sm'
        aria-label='Próxima página'
        disabled={page >= totalPages}
        onClick={() => {
          onChange(page + 1);
        }}
      />
    </div>
  );
};
